/* Free-shipping progress. Recomputes from the cart after any change.
   Redesign feedback round 3, item F: floor raised again, from 20% to 30%
   (scale correspondingly dropped from 80 to 70, so the bar still reads
   100% exactly at the threshold). Deepening the fill colour and taking the
   track to 10px (see assets/component-gp-shipping-progress.css) fixed
   contrast and thinness, but at 20% the floor segment was still short
   enough, against a ~400px rendered bar, to read as a stub rather than a
   deliberate "you've started" segment. This must match
   snippets/shipping-progress-gp.liquid's Liquid computation exactly: the
   two render the same bar at different points in the cart's lifecycle
   (initial page load vs. a live cart update), and drifting the constants
   apart would make the bar visibly jump on the first update. */
class ShippingProgress extends HTMLElement {
  connectedCallback() {
    this.threshold = parseInt(this.dataset.threshold, 10) || 0;
    this.text = this.querySelector('[data-sp-text]');
    this.fill = this.querySelector('[data-sp-fill]');
    this.onCartUpdate = this.onCartUpdate.bind(this);
    /* This theme does NOT dispatch DOM CustomEvents for cart changes. Dawn's
       mechanism is the in-memory pub/sub in assets/pubsub.js, keyed by
       PUB_SUB_EVENTS.cartUpdate ('cart-update') from assets/constants.js —
       see assets/cart.js. Both are plain scripts loaded before this one in
       layout/theme.liquid, so `subscribe` and `PUB_SUB_EVENTS` are globals
       here. An addEventListener on `document` would never fire. */
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
    this.hideOutsideServedMarkets();
    this.initPromo();
  }

  /* Timed free-shipping promotion (Theme settings > Cart). Liquid draws the
     right state for the moment the page was built; a cached page can be
     stale, so the browser's clock decides again on load and at the window's
     edges. data-promo-start / -end are epoch seconds. */
  promoNow() {
    const start = Number(this.dataset.promoStart);
    const end = Number(this.dataset.promoEnd);
    if (!start || !end || end <= start) return false;
    const now = Date.now() / 1000;
    return now >= start && now < end;
  }

  initPromo() {
    if (!this.dataset.promoStart) return;
    this.syncPromo();
    const now = Date.now() / 1000;
    [Number(this.dataset.promoStart), Number(this.dataset.promoEnd)].forEach((edge) => {
      const wait = (edge - now) * 1000;
      if (wait > 0 && wait < 2147483000) window.setTimeout(() => this.syncPromo(true), wait + 500);
    });
  }

  syncPromo(force) {
    const on = this.promoNow();
    if (!force && on === (this.dataset.promoState === 'on')) return;
    this.dataset.promoState = on ? 'on' : 'off';
    if (on) {
      this.renderPromo();
    } else {
      fetch(`${window.Shopify.routes.root}cart.js`)
        .then((r) => r.json())
        .then((data) => this.update(data))
        .catch(() => {});
    }
  }

  renderPromo() {
    if (this.fill) this.fill.style.width = '100%';
    if (this.text) this.text.textContent = this.dataset.promoText;
    this.setNote(true);
  }

  setNote(promo) {
    const el = this.querySelector('[data-sp-note]');
    if (!el) return;
    const text = promo ? this.dataset.notePromo : this.dataset.noteNormal;
    el.textContent = text || '';
    el.hidden = !text;
  }

  /* The Liquid gate (localization.country == 'AU') cannot see a visitor
     from a country the store does not serve at all — the US, by design.
     Shopify falls such visitors back to the primary market, so they browse
     as "AU" in AUD and the bar promised them free shipping they can never
     get (merchant test over a US VPN, feedback round 11). Shopify's own IP
     detection still reports the real country; it is compared against the
     countries the store serves, which layout/theme.liquid prints on <html>
     as data-served-countries (localization.available_countries). If the
     detected country is not on that list, the bar hides. The suggestions'
     own country options are a fallback only, for a page without that
     attribute. A served-market visitor (an NZ shopper who chose
     AU, say) is left alone — the localization they picked is authoritative.
     Fails open: no answer, no change. One request per session, shared by
     every bar on the page.

     The answer is cached in sessionStorage, and an unserved one also sets
     html[data-unserved]: the inline script in layout/theme.liquid's <head>
     sets the same attribute from the cache before first paint on later
     pages, so the CSS hides these promises with no layout shift. The cache
     key carries a version — bump it whenever the detection changes, so an
     answer computed the old way is ignored. */
  hideOutsideServedMarkets() {
    ShippingProgress.detectUnserved().then((unserved) => {
      if (unserved) this.hidden = true;
    });
  }

  static detectUnserved() {
    if (ShippingProgress.unservedPromise) return ShippingProgress.unservedPromise;
    const key = 'gp-unserved-country-v2';
    let cached = null;
    try {
      cached = sessionStorage.getItem(key);
    } catch (e) {}
    ShippingProgress.unservedPromise =
      cached !== null ? Promise.resolve(cached === '1') : ShippingProgress.fetchUnserved(key);
    ShippingProgress.unservedPromise.then((unserved) => {
      if (unserved) document.documentElement.dataset.unserved = '';
    });
    return ShippingProgress.unservedPromise;
  }

  static fetchUnserved(key) {
    const url = `${window.Shopify.routes.root}browsing_context_suggestions.json?country[enabled]=true`;
    return fetch(url)
      .then((r) => r.json())
      .then((data) => {
        const detected = data && data.detected_values && data.detected_values.country;
        if (!detected || !detected.handle) return false;
        const servedList = document.documentElement.dataset.servedCountries;
        let unserved;
        if (servedList) {
          unserved = !servedList.split(',').includes(detected.handle);
        } else {
          const suggestion = data.suggestions && data.suggestions[0];
          const options = suggestion && suggestion.parts && suggestion.parts.country && suggestion.parts.country.options;
          if (!options) return false;
          unserved = !Object.prototype.hasOwnProperty.call(options, detected.handle);
        }
        try {
          sessionStorage.setItem(key, unserved ? '1' : '0');
        } catch (e) {}
        return unserved;
      })
      .catch(() => false);
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  /* Dawn publishes cartUpdate with a cartData payload. Use it when it is the
     whole cart and fall back to a fetch only when it is not (an add publishes
     the added line), so the common path costs no request. */
  onCartUpdate(event) {
    const cart = event && event.cartData;
    if (cart && typeof cart.total_price === 'number' && Array.isArray(cart.items)) {
      this.update(cart);
      return;
    }
    fetch(`${window.Shopify.routes.root}cart.js`)
      .then((r) => r.json())
      .then((data) => this.update(data))
      .catch(() => {});
  }

  /* data-only-if-shipping (the cart drawer): hide while the cart holds items
     but none that ships — a gift card on its own. */
  update(cart) {
    if (this.hasAttribute('data-only-if-shipping')) {
      this.toggleAttribute('data-no-shipping', cart.item_count > 0 && !cart.requires_shipping);
    }
    this.render(ShippingProgress.shippableTotal(cart));
  }

  /* Only what ships counts toward free shipping: Shopify leaves gift cards out
     of the order value (mirrors snippets/shipping-progress-gp.liquid). */
  static shippableTotal(cart) {
    const unshipped = cart.items
      .filter((item) => !item.requires_shipping)
      .reduce((sum, item) => sum + item.final_line_price, 0);
    return Math.max(0, cart.total_price - unshipped);
  }

  render(totalCents) {
    if (this.dataset.promoStart) {
      if (this.promoNow()) {
        this.renderPromo();
        return;
      }
      this.setNote(false);
    }
    if (!this.threshold) return;
    const remaining = Math.max(0, this.threshold - totalCents);
    /* Empty cart, empty track: the 30% floor only applies once something is
       in the cart (mirrors snippets/shipping-progress-gp.liquid). */
    const pct = totalCents > 0 ? Math.min(100, 30 + (totalCents / this.threshold) * 70) : 0;
    if (this.fill) this.fill.style.width = `${pct}%`;
    if (!this.text) return;
    this.text.textContent =
      remaining === 0
        ? this.dataset.unlocked
        : this.dataset.remaining.replace('[amount]', this.money(remaining));
  }

  money(cents) {
    const dollars = cents / 100;
    return `$${dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2)}`;
  }
}

if (!customElements.get('shipping-progress')) {
  customElements.define('shipping-progress', ShippingProgress);
}

/* The header's brand-line strip (sections/brand-line-bar.liquid) carries the
   same AU-only free-shipping promise, gated the same way in Liquid. It is
   not a <shipping-progress>, so it borrows the same check here: flagged
   data-unserved, its CSS hides the strip. Deferred script, so the
   header markup is already parsed. */
document.querySelectorAll('[data-au-shipping]').forEach((bar) => {
  ShippingProgress.detectUnserved().then((unserved) => {
    if (unserved) bar.setAttribute('data-unserved', '');
  });

  /* The same timed promotion, in the strip's shipping line (see
     ShippingProgress.promoNow). */
  const start = Number(bar.dataset.promoStart);
  const end = Number(bar.dataset.promoEnd);
  const line = bar.querySelector('[data-strip-shipping]');
  if (!start || !end || end <= start || !line) return;
  const sync = () => {
    const now = Date.now() / 1000;
    line.textContent = now >= start && now < end ? bar.dataset.promoText : bar.dataset.normalText;
  };
  sync();
  [start, end].forEach((edge) => {
    const wait = (edge - Date.now() / 1000) * 1000;
    if (wait > 0 && wait < 2147483000) window.setTimeout(sync, wait + 500);
  });
});
